import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Typography,
  Dialog,
  Card,
  Grid,
  Paper,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  getEarnedLeaveRequestsByUserId,
  deleteEarnedLeaveRequest,
} from "../../services/earnedLeaveRequest.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import ApplyEarnedLeave from "./ApplyEarnedLeave";
import Styles from "../Employee/Employee.module.scss";

const MyEarnedLeaveRequests = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState([]);
  const [tableTotalCount, setTableTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);

  const getUserIdFromToken = () => {
    if (!accessToken) return null;

    try {
      const decoded = jwtDecode(accessToken);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  };

  const userId = getUserIdFromToken();

  const getData = async () => {
    try {
      const res = await getEarnedLeaveRequestsByUserId(userId);
      const modifiedData = res?.data?.data.map((e) => ({
        ...e,
        dateFormatted: `${dayjs(e.date).format("DD/MM/YYYY")} (${dayjs(e.date).format("dddd")})`,
        rejectionReason: e.rejectionReason 
          ? e.rejectionReason 
          : e.status === "Approved" 
          ? "Approved - 1 earned leave added" 
          : "Pending review",
        createdAtFormatted: dayjs(e.createdAt).format("DD/MM/YYYY HH:mm"),
      }));
      setData(modifiedData || []);
      setTableTotalCount(modifiedData?.length || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      ToastError("Failed to fetch your requests");
    }
  };

  useEffect(() => {
    if (userId) {
      getData();
    }
  }, [userId]);

  const handleAdd = () => {
    setOpenDialog(true);
  };

  const handleDelete = async (row) => {
    if (row.status !== "Pending") {
      ToastError("Only pending requests can be deleted");
      return;
    }

    try {
      const res = await deleteEarnedLeaveRequest(row._id);
      ToastSuccess(res.message);
      getData();
    } catch (error) {
      ToastError(error?.data?.message || "Failed to delete request");
    }
  };

  const columns = [
    {
      id: "dateFormatted",
      label: "Weekend Date",
      minWidth: 150,
    },
    {
      id: "reason",
      label: "Reason for Working",
      minWidth: 250,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
    },
    {
      id: "rejectionReason",
      label: "Admin Response",
      minWidth: 200,
    },
    {
      id: "createdAtFormatted",
      label: "Requested On",
      minWidth: 150,
    },
  ];

  const actions = [
    {
      label: "Delete",
      onClick: handleDelete,
      condition: (row) => row.status === "Pending",
    },
  ];

  const tableConfig = {
    isAction: true,
    isDelete: true,
  };

  // Calculate statistics
  const stats = {
    total: data.length,
    pending: data.filter((r) => r.status === "Pending").length,
    approved: data.filter((r) => r.status === "Approved").length,
    rejected: data.filter((r) => r.status === "Rejected").length,
  };

  return (
    <Box className={Styles.Page}>
      <Box className={Styles.pageHeader} sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <Typography className={Styles.pageTitle} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventAvailableIcon sx={{ fontSize: 32, color: "primary.main" }} />
              Earned Leave Requests
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Request earned leave for working on weekends (Saturday/Sunday)
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={handleAdd}
            size="large"
            sx={{
              textTransform: "none",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            Request Earned Leave
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, bgcolor: "#f5f5f5", borderLeft: "4px solid #2196f3" }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Total Requests
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "#2196f3" }}>
                {stats.total}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, bgcolor: "#fff8e1", borderLeft: "4px solid #ff9800" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <PendingActionsIcon sx={{ fontSize: 18, color: "#ff9800" }} />
                <Typography variant="body2" color="textSecondary">
                  Pending
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "#ff9800" }}>
                {stats.pending}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, bgcolor: "#e8f5e9", borderLeft: "4px solid #4caf50" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                <Typography variant="body2" color="textSecondary">
                  Approved
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "#4caf50" }}>
                {stats.approved}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, bgcolor: "#ffebee", borderLeft: "4px solid #f44336" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <CancelIcon sx={{ fontSize: 18, color: "#f44336" }} />
                <Typography variant="body2" color="textSecondary">
                  Rejected
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "#f44336" }}>
                {stats.rejected}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <MasterCommonTable
        tableConfig={tableConfig}
        columns={columns}
        data={data}
        tableTotalCount={tableTotalCount}
        handleDelete={handleDelete}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <ApplyEarnedLeave setOpenDialog={setOpenDialog} GetData={getData} />
      </Dialog>
    </Box>
  );
};

export default MyEarnedLeaveRequests;
